import React from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiShield, FiCheck, FiLock, FiEyeOff, FiHeart } from "react-icons/fi";
import "./PrivacyPolicyLayer.css";

const PrivacyPolicyLayer = () => {
  const commitments = [
    "We collect only the minimum information required to operate the application.",
    "We never sell your personal information.",
    "We never rent your personal information.",
    "We never misuse your personal information.",
    "We never access your private files without your permission.",
    "We never read your SMS messages.",
    "We never access your call history.",
    "We never access your contacts unless explicitly required and permitted.",
    "We never record audio without your permission.",
    "We never record video without your permission.",
    "We never collect banking or payment information unless an official payment feature is integrated.",
    "We never collect passwords from other applications or services.",
    "We never collect biometric information unless explicitly required by a feature and disclosed separately.",
    "We use secure communication methods to protect your information.",
    "We are committed to maintaining the confidentiality, integrity, and security of all user information entrusted to us."
  ];

  return (
    <div className="privacy-page-wrapper py-4 px-3">
      <div className="privacy-container">

        {/* Header Card */}
        <div className="privacy-header-card mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <div className="privacy-brand-icon">
              <FiShield />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h4 className="privacy-title">Privacy Policy</h4>
                <span className="privacy-status-badge">
                  <span className="status-dot"></span> Official & Active
                </span>
              </div>
              <p className="text-muted mb-0 text-sm">
                This App &bull; Clear, transparent & parent-first data commitments
              </p>
            </div>
          </div>
          <Link to="/sign-in" className="btn-back-signin">
            <FiArrowLeft /> Back to Sign In
          </Link>
        </div>

        {/* Highlights Row */}
        <div className="privacy-highlights-grid">
          <div className="highlight-card blue-card">
            <div className="highlight-icon blue">
              <FiEyeOff />
            </div>
            <div className="highlight-text">
              <h6>Zero Data Selling</h6>
              <p>We never sell or rent user data</p>
            </div>
          </div>
          <div className="highlight-card green-card">
            <div className="highlight-icon green">
              <FiLock />
            </div>
            <div className="highlight-text">
              <h6>Encrypted Traffic</h6>
              <p>All server communication is SSL encrypted</p>
            </div>
          </div>
          <div className="highlight-card purple-card">
            <div className="highlight-icon purple">
              <FiHeart />
            </div>
            <div className="highlight-text">
              <h6>Children's Safety</h6>
              <p>Built strictly for parent & school connection</p>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="privacy-content-card">

          {/* 1. Introduction */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">1</span>
              <h5 className="section-title">Introduction</h5>
            </div>
            <p className="privacy-text">
              Welcome to <strong>This App</strong>.
            </p>
            <p className="privacy-text">
              This App is developed to provide parents with a secure and convenient way to stay connected with their child's school transportation and school-related services. We are committed to protecting your privacy and ensuring that your personal information remains safe and secure.
            </p>
            <p className="privacy-text">
              We respect your privacy and believe that every user has the right to know what information is collected, why it is collected, and how it is used. This Privacy Policy explains our practices regarding data collection, storage, usage, and protection.
            </p>
            <p className="privacy-text">
              By installing or using this application, you agree to the terms described in this Privacy Policy.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 2. Information We Collect */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">2</span>
              <h5 className="section-title">Information We Collect</h5>
            </div>
            <p className="privacy-text">
              We collect only the minimum information that is necessary for providing the services offered by the application.
            </p>
            <p className="privacy-text">
              Depending on the features used, we may collect information such as your name, mobile number, email address, student details, login credentials, and other information required for authentication and communication with the school.
            </p>
            <p className="privacy-text">
              We do not collect any information that is unrelated to the functionality of the application.
            </p>
            <p className="privacy-text">
              We do not collect sensitive personal information such as your bank account details, debit card or credit card numbers, passwords of other services, Aadhaar number, PAN number, biometric information, fingerprints, facial recognition data, health records, medical information, financial transactions, personal messages, contacts, call history, SMS messages, browsing history, or any other confidential personal information.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 3. Location Information */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">3</span>
              <h5 className="section-title">Location Information</h5>
            </div>
            <p className="privacy-text">
              If you choose to allow location permission, the application may access your device's location only to provide location-based features such as school bus tracking, route monitoring, pickup status, and estimated arrival information.
            </p>
            <p className="privacy-text">
              Location information is used only while providing these services.
            </p>
            <p className="privacy-text">
              We do not continuously monitor your location for advertising, marketing, or profiling purposes.
            </p>
            <p className="privacy-text">
              We do not sell or rent your location information to any third party.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 4. Camera Permission */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">4</span>
              <h5 className="section-title">Camera Permission</h5>
            </div>
            <p className="privacy-text">
              If you grant camera permission, it is used only for features provided within the application, such as scanning QR codes, uploading profile photographs, or capturing documents where applicable.
            </p>
            <p className="privacy-text">
              Images captured through the camera remain under your control and are used only for the intended feature.
            </p>
            <p className="privacy-text">
              The application never records videos or captures images without your knowledge.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 5. Storage and Media Access */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">5</span>
              <h5 className="section-title">Storage and Media Access</h5>
            </div>
            <p className="privacy-text">
              If storage permission is granted, the application may access files only when you choose to upload documents, download notices, save reports, or share images.
            </p>
            <p className="privacy-text">
              The application does not scan your personal files, photos, videos, or documents stored on your device.
            </p>
            <p className="privacy-text">
              No files are accessed without your permission.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 6. Internet Access */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">6</span>
              <h5 className="section-title">Internet Access</h5>
            </div>
            <p className="privacy-text">
              Internet permission is required only to communicate securely with our servers and provide services such as login, notifications, bus tracking, attendance updates, and downloading school information.
            </p>
            <p className="privacy-text">
              All communication between the application and the server is performed using secure encrypted connections wherever applicable.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 7. Notifications */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">7</span>
              <h5 className="section-title">Notifications</h5>
            </div>
            <p className="privacy-text">
              The application may send notifications related to school announcements, transport updates, attendance alerts, emergency notices, fee reminders, holiday information, and other important updates.
            </p>
            <p className="privacy-text">
              We do not use notifications for spam, promotional advertisements, or unrelated marketing messages.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 8. How We Use Your Information */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">8</span>
              <h5 className="section-title">How We Use Your Information</h5>
            </div>
            <p className="privacy-text">
              The information collected through the application is used only for legitimate educational and transportation-related purposes.
            </p>
            <p className="privacy-text">
              This includes verifying user identity, providing secure login access, displaying student information, tracking school buses, sending important notifications, improving application performance, responding to support requests, and maintaining system security.
            </p>
            <p className="privacy-text">
              Your information is never used for unauthorized advertising or commercial profiling.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 9. Data Sharing */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">9</span>
              <h5 className="section-title">Data Sharing</h5>
            </div>
            <p className="privacy-text">
              We respect your privacy.
            </p>
            <p className="privacy-text">
              We do not sell, rent, trade, or share your personal information with advertising companies, marketing agencies, or unrelated third parties.
            </p>
            <p className="privacy-text">
              Information may be shared only when necessary to provide the requested service, comply with legal obligations, protect user safety, or operate the application securely through trusted service providers.
            </p>
            <p className="privacy-text">
              Any service provider working with us is required to maintain appropriate security and confidentiality.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 10. Third-Party Services */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">10</span>
              <h5 className="section-title">Third-Party Services</h5>
            </div>
            <p className="privacy-text">
              The application may use trusted third-party services such as Firebase Cloud Messaging, Google Play Services, Google Maps, or other Google technologies solely for delivering application features.
            </p>
            <p className="privacy-text">
              These services operate under their own privacy policies.
            </p>
            <p className="privacy-text">
              We do not authorize third-party providers to use your personal information for advertising or unrelated purposes.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 11. Children's Privacy */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">11</span>
              <h5 className="section-title">Children's Privacy</h5>
            </div>
            <p className="privacy-text">
              This application is intended for parents or authorized guardians of students.
            </p>
            <p className="privacy-text">
              Student-related information displayed in the application is provided by the respective educational institution.
            </p>
            <p className="privacy-text">
              We do not knowingly collect personal information directly from children without authorization from the school or parent.
            </p>
            <p className="privacy-text">
              Protecting children's privacy is one of our highest priorities.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 12. Data Security */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">12</span>
              <h5 className="section-title">Data Security</h5>
            </div>
            <p className="privacy-text">
              We implement appropriate technical and organizational security measures to protect your information from unauthorized access, misuse, alteration, disclosure, or destruction.
            </p>
            <p className="privacy-text">
              Our systems use secure authentication mechanisms, encrypted communication, controlled server access, and regular monitoring to maintain data security.
            </p>
            <p className="privacy-text">
              Although no electronic system can guarantee absolute security, we continuously work to safeguard user information using industry-standard practices.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 13. Data Retention */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">13</span>
              <h5 className="section-title">Data Retention</h5>
            </div>
            <p className="privacy-text">
              Personal information is retained only for as long as necessary to provide the requested services, comply with legal requirements, resolve disputes, or enforce our agreements.
            </p>
            <p className="privacy-text">
              Once the information is no longer required, it is securely deleted or anonymized wherever applicable.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 14. User Control */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">14</span>
              <h5 className="section-title">User Control</h5>
            </div>
            <p className="privacy-text">
              Users have the right to review, update, or request correction of their personal information whenever applicable.
            </p>
            <p className="privacy-text">
              Users may also request deletion of their account and associated personal information by contacting the application administrator or support team, subject to applicable legal and operational requirements.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 15. Data Deletion */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">15</span>
              <h5 className="section-title">Data Deletion</h5>
            </div>
            <p className="privacy-text">
              Upon receiving a valid deletion request, we will permanently remove or anonymize eligible personal information within a reasonable period unless retention is required by applicable law or necessary for legitimate operational purposes.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 16. Advertising */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">16</span>
              <h5 className="section-title">Advertising</h5>
            </div>
            <p className="privacy-text">
              This App does not display third-party advertisements intended for behavioral targeting.
            </p>
            <p className="privacy-text">
              We do not use your personal information for advertising personalization or commercial marketing.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 17. Analytics */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">17</span>
              <h5 className="section-title">Analytics</h5>
            </div>
            <p className="privacy-text">
              If application analytics are used, they are collected only to understand application performance, identify technical issues, improve stability, and enhance the user experience.
            </p>
            <p className="privacy-text">
              Analytics information is not used to identify individual users for marketing purposes.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 18. Cookies and Tracking Technologies */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">18</span>
              <h5 className="section-title">Cookies and Tracking Technologies</h5>
            </div>
            <p className="privacy-text">
              The mobile application does not use cookies in the same manner as websites.
            </p>
            <p className="privacy-text">
              Any technical identifiers required for secure login, notifications, or application functionality are used solely for providing core services.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 19. International Data Transfers */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">19</span>
              <h5 className="section-title">International Data Transfers</h5>
            </div>
            <p className="privacy-text">
              Where applicable, data may be processed using secure cloud infrastructure located in different regions. Appropriate safeguards are implemented to protect personal information during such processing.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 20. Policy Updates */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">20</span>
              <h5 className="section-title">Policy Updates</h5>
            </div>
            <p className="privacy-text">
              We may update this Privacy Policy from time to time to reflect changes in legal requirements, application functionality, or security practices.
            </p>
            <p className="privacy-text">
              Users are encouraged to review this policy periodically.
            </p>
            <p className="privacy-text">
              Continued use of the application after any update constitutes acceptance of the revised policy.
            </p>
          </div>

          <hr className="privacy-divider" />

          {/* 21. Contact Information */}
          <div className="privacy-section">
            <div className="privacy-section-header">
              <span className="section-number-badge">21</span>
              <h5 className="section-title">Contact Information</h5>
            </div>
            <p className="privacy-text">
              If you have any questions, concerns, suggestions, or requests regarding this Privacy Policy or the handling of your information, please contact us through the official support channels provided by This App.
            </p>
          </div>

          {/* Additional Privacy Commitments Banner */}
          <div className="commitments-wrapper">
            <div className="commitments-header">
              <div className="commitments-header-icon">
                <FiShield />
              </div>
              <div>
                <h4>Additional Privacy Commitments</h4>
                <p className="commitments-subtitle">
                  Our strict standards and zero-tolerance policies regarding user data
                </p>
              </div>
            </div>
            <div className="commitments-grid">
              {commitments.map((commitment, idx) => (
                <div key={idx} className="commitment-item">
                  <div className="commitment-check-icon">
                    <FiCheck />
                  </div>
                  <span className="commitment-text">{commitment}</span>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="privacy-footer">
          <p className="mb-0">
            &copy; {new Date().getFullYear()} This App. All rights reserved.
          </p>
        </div>

      </div>
    </div>
  );
};

export default PrivacyPolicyLayer;
